import { mentionPayloadFromUrl, mentionUrl } from "./mentionUrl";
import {
	type Block,
	type Inline,
	inlineAugmented,
	inlineFromPayload,
	inlineSource,
} from "./questionDocument";

/**
 * The clipboard's `text/html` flavour: a prompt as readable rich text whose
 * mentions are links (see mentionUrl).
 *
 * This is what makes a prompt portable. `text/plain` stays human-readable, so
 * pasting into a chat or a document reads as prose; the HTML carries the atoms'
 * identity in link hrefs, which is the one thing hosts like Slack preserve. A
 * prompt can therefore be pasted into a conversation, copied back out on another
 * machine, and still arrive with its mentions intact.
 *
 * Reading is deliberately narrow: the HTML path is used only when the markup
 * contains at least one of our mention links. Pasting from anywhere else keeps
 * using `text/plain`, so this cannot change how pasting from a browser, Word or
 * a spreadsheet behaves.
 */

const escapeHtml = (text: string): string =>
	text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");

/**
 * An inline as HTML: an atom becomes a mention link, text is escaped, and an
 * atom with no addressable id falls back to its readable label.
 *
 * The one place the link's shape is decided. Anything that puts a mention on
 * the clipboard goes through here — including the chat, whose rendered chip is
 * rewritten into this form on copy (see chat/utils/mentionClipboard) — so every
 * copy of a mention is the same bytes, whatever it was copied from.
 */
export function inlineToHtml(inline: Inline): string {
	const text = escapeHtml(inlineSource(inline));
	if (inline.kind === "text") return text;
	const href = mentionUrl(inline);
	return href === null ? text : `<a href="${escapeHtml(href)}">${text}</a>`;
}

/** A block's content as HTML: atoms become mention links, text is escaped. */
function inlinesToHtml(inlines: Inline[]): string {
	return inlines.map(inlineToHtml).join("");
}

/** The element a block kind renders as, and whether it is a list item. */
function blockTag(block: Block): { tag: string; list: "ul" | "ol" | null } {
	switch (block.kind.type) {
		case "heading":
			return { tag: `h${block.kind.level}`, list: null };
		case "quote":
			return { tag: "blockquote", list: null };
		case "list-ul":
			return { tag: "li", list: "ul" };
		case "list-ol":
			return { tag: "li", list: "ol" };
		default:
			return { tag: "p", list: null };
	}
}

/** Serialises selected blocks as the clipboard's HTML flavour. */
export function clipboardHtmlForBlocks(blocks: Block[]): string {
	const parts: string[] = [];
	let openList: "ul" | "ol" | null = null;

	for (const block of blocks) {
		const { tag, list } = blockTag(block);
		if (list !== openList) {
			if (openList) parts.push(`</${openList}>`);
			if (list) parts.push(`<${list}>`);
			openList = list;
		}
		parts.push(`<${tag}>${inlinesToHtml(block.inlines)}</${tag}>`);
	}
	if (openList) parts.push(`</${openList}>`);

	return parts.join("");
}

/** Elements whose structure the document model can express as a block marker. */
const BLOCK_MARKERS: Record<string, string> = {
	H1: "# ",
	H2: "## ",
	H3: "### ",
	H4: "#### ",
	H5: "##### ",
	H6: "###### ",
	BLOCKQUOTE: "> ",
};

const BLOCK_TAGS = new Set([
	...Object.keys(BLOCK_MARKERS),
	"LI",
	"P",
	"DIV",
	"TR",
	"PRE",
	"SECTION",
	"ARTICLE",
	"UL",
	"OL",
	"TABLE",
	"BODY",
]);

const appendText = (lines: string[], text: string): void => {
	lines[lines.length - 1] += text;
};

const isBlankLine = (lines: string[]): boolean =>
	lines[lines.length - 1].trim().length === 0;

/** The marker an element implies, resolving `li` against the list it sits in. */
function markerFor(element: Element): string | undefined {
	if (element.tagName === "LI") {
		return element.parentElement?.tagName === "OL" ? "1. " : "- ";
	}
	return BLOCK_MARKERS[element.tagName];
}

function walk(node: Node, lines: string[]): void {
	if (node.nodeType === Node.TEXT_NODE) {
		// Collapse HTML whitespace the way a renderer would: newlines and runs of
		// spaces in the markup are formatting, not content. Whitespace between
		// block elements is dropped entirely so it can't stop the next element's
		// marker from landing at the start of its line.
		const text = (node.textContent ?? "").replace(/\s+/g, " ");
		if (text.trim().length === 0 && isBlankLine(lines)) return;
		if (text.length > 0) appendText(lines, text);
		return;
	}
	if (!(node instanceof Element)) return;

	if (node.tagName === "BR") {
		lines.push("");
		return;
	}

	if (node.tagName === "A") {
		const payload = mentionPayloadFromUrl(node.getAttribute("href") ?? "");
		const inline = payload ? inlineFromPayload(payload) : null;
		if (inline) {
			appendText(lines, inlineAugmented(inline));
			return;
		}
		// Any other link contributes its text, as a plain-text paste would.
	}

	const isBlock = BLOCK_TAGS.has(node.tagName);
	if (isBlock && !isBlankLine(lines)) lines.push("");
	const marker = markerFor(node);
	if (marker && lines[lines.length - 1].length === 0) appendText(lines, marker);

	for (const child of node.childNodes) walk(child, lines);

	if (isBlock && !isBlankLine(lines)) lines.push("");
}

/**
 * The prompt text (block markers plus inline mention JSON) described by pasted
 * HTML, or null when it carries none of our mention links — in which case the
 * caller should fall back to the clipboard's plain text.
 */
export function augmentedTextFromClipboardHtml(html: string): string | null {
	if (html.trim().length === 0) return null;

	// A detached document: no scripts run and no resources load, and we only ever
	// read text and hrefs out of it — the markup never reaches the live DOM.
	const parsed = new DOMParser().parseFromString(html, "text/html");
	const hasMention = Array.from(parsed.querySelectorAll("a[href]")).some(
		(anchor) =>
			mentionPayloadFromUrl(anchor.getAttribute("href") ?? "") != null,
	);
	if (!hasMention) return null;

	const lines: string[] = [""];
	walk(parsed.body, lines);

	while (lines.length > 0 && lines[lines.length - 1].trim().length === 0) {
		lines.pop();
	}
	return lines.length > 0 ? lines.join("\n") : null;
}
